const DEFAULT_CHUNK_SIZE = 1500
const DEFAULT_MAX_STRING_LENGTH = 512
const DEFAULT_SENSITIVE_KEY_PATTERN =
  /(authorization|token|password|passwd|pwd|pin|secret|sign|aeskey|aesiv|publicpem|privatekey)/i
const DEFAULT_TRIM_KEY_PATTERN = /(front_page_img|back_page_img|image|img|base64|photo|portrait|body)/i

function isPlainObject(value) {
  return Object.prototype.toString.call(value) === '[object Object]'
}

function normalizeError(error) {
  if (!error) {
    return {
      message: ''
    }
  }

  if (error instanceof Error) {
    const normalizedError = {
      message: error.message || String(error)
    }

    if (error.code !== undefined && error.code !== null && error.code !== '') {
      normalizedError.code = error.code
    }

    if (error.name) {
      normalizedError.name = error.name
    }

    return normalizedError
  }

  if (isPlainObject(error)) {
    return error
  }

  return {
    message: String(error)
  }
}

function formatTrimmedValue(value) {
  const normalizedValue = String(value)
  return `[已裁切 length=${normalizedValue.length} prefix=${normalizedValue.slice(
    0,
    32
  )} suffix=${normalizedValue.slice(-16)}]`
}

function sanitizePayload(payload, options = {}, currentKey = '', visited = new WeakMap()) {
  const {
    maxStringLength = DEFAULT_MAX_STRING_LENGTH,
    sensitiveKeyPattern = DEFAULT_SENSITIVE_KEY_PATTERN,
    trimKeyPattern = DEFAULT_TRIM_KEY_PATTERN
  } = options

  if (payload == null) {
    return payload
  }

  if (sensitiveKeyPattern.test(currentKey)) {
    return '[已隐藏]'
  }

  if (payload instanceof Error) {
    return normalizeError(payload)
  }

  if (typeof payload === 'string') {
    if (trimKeyPattern.test(currentKey) || payload.length > maxStringLength) {
      return formatTrimmedValue(payload)
    }

    return payload
  }

  if (typeof payload === 'bigint') {
    return String(payload)
  }

  if (typeof payload === 'function') {
    return `[Function ${payload.name || 'anonymous'}]`
  }

  if (typeof payload !== 'object') {
    return payload
  }

  if (visited.has(payload)) {
    return visited.get(payload)
  }

  if (Array.isArray(payload)) {
    const nextArray = []
    visited.set(payload, nextArray)

    payload.forEach(item => {
      nextArray.push(sanitizePayload(item, options, currentKey, visited))
    })

    return nextArray
  }

  if (!isPlainObject(payload)) {
    return String(payload)
  }

  const nextObject = {}
  visited.set(payload, nextObject)

  Object.keys(payload).forEach(key => {
    nextObject[key] = sanitizePayload(payload[key], options, key, visited)
  })

  return nextObject
}

function safeStringify(payload, options = {}) {
  if (typeof payload === 'string') {
    return payload
  }

  try {
    const serialized = JSON.stringify(sanitizePayload(payload, options))
    return serialized === undefined ? '' : serialized
  } catch (error) {
    return JSON.stringify({
      message: '日志序列化失败',
      error: normalizeError(error)
    })
  }
}

function normalizeLabel(label = '') {
  const normalizedLabel = String(label || '').trim()
  return normalizedLabel || '[debug]'
}

export class DebugLogger {
  constructor(options = {}) {
    this.options = {
      chunkSize: DEFAULT_CHUNK_SIZE,
      maxStringLength: DEFAULT_MAX_STRING_LENGTH,
      sensitiveKeyPattern: DEFAULT_SENSITIVE_KEY_PATTERN,
      trimKeyPattern: DEFAULT_TRIM_KEY_PATTERN,
      ...options
    }
  }

  log(label, payload, options = {}) {
    this._print('log', label, payload, options)
  }

  info(label, payload, options = {}) {
    this.log(label, payload, options)
  }

  warn(label, payload, options = {}) {
    this._print('warn', label, payload, options)
  }

  error(label, payload, options = {}) {
    this._print('error', label, payload, options)
  }

  exception(label, error, extra = {}, options = {}) {
    this._print(
      'error',
      label,
      {
        ...extra,
        error: normalizeError(error)
      },
      options
    )
  }

  _print(level, label, payload, options = {}) {
    const logger = typeof console[level] === 'function' ? console[level] : console.log
    const normalizedLabel = normalizeLabel(label)

    if (payload === undefined) {
      logger(normalizedLabel)
      return
    }

    const serialized = safeStringify(payload, { ...this.options, ...options })
    const chunkSize = Number(options.chunkSize || this.options.chunkSize || DEFAULT_CHUNK_SIZE)

    if (!serialized) {
      logger(normalizedLabel)
      return
    }

    if (serialized.length <= chunkSize) {
      logger(`${normalizedLabel}:`, serialized)
      return
    }

    const totalChunks = Math.ceil(serialized.length / chunkSize)
    for (let index = 0; index < totalChunks; index++) {
      const start = index * chunkSize
      const end = start + chunkSize
      logger(`${normalizedLabel} chunk ${index + 1}/${totalChunks}:`, serialized.slice(start, end))
    }
  }
}

const debugLogger = new DebugLogger()

export function buildDebugErrorPayload(error, extra = {}) {
  return {
    ...extra,
    error: normalizeError(error)
  }
}

export default debugLogger
